import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  TouchableWithoutFeedback,
  Animated,
  StyleSheet,
  Platform,
} from 'react-native';
import { Svg, Path } from 'react-native-svg';

import { Box, Text } from '@components/Restyle';
import theme from '@themes/theme';

export const EnhancedAnimatedCheckbox = ({
  isChecked: externalIsChecked,
  isHovered: externalIsHovered,
  onValueChange,
  onHoverChange,
  label,
  isRadioButton = false,
  isHighlightColorGreen = true,
  accessibilityLabel,
}: {
  isChecked?: boolean;
  isHovered?: boolean;
  isRadioButton?: boolean;
  label?: string;
  onValueChange?: (newValue: boolean) => void;
  onHoverChange?: (isHovered: boolean) => void;
  isHighlightColorGreen?: boolean;
  accessibilityLabel?: string;
}) => {
  const [internalIsChecked, setInternalIsChecked] = useState(
    externalIsChecked || false
  );
  const [internalIsHovered, setInternalIsHovered] = useState(
    externalIsHovered || false
  );

  const scaleAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (externalIsChecked !== undefined) {
      setInternalIsChecked(externalIsChecked);
    }
  }, [externalIsChecked]);

  useEffect(() => {
    if (externalIsHovered !== undefined) {
      setInternalIsHovered(externalIsHovered);
    }
  }, [externalIsHovered]);

  const isChecked = externalIsChecked ?? internalIsChecked;
  const isHovered = externalIsHovered ?? internalIsHovered;

  const handlePress = () => {
    const newValue = !isChecked;
    triggerAnimation();
    if (externalIsChecked !== undefined) {
      onValueChange?.(newValue);
    } else {
      setInternalIsChecked(newValue);
      onValueChange?.(newValue);
    }
  };

  const triggerAnimation = () => {
    scaleAnim.setValue(1);
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 1.3,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const handleMouseEnter = () => {
    if (externalIsHovered === undefined) {
      setInternalIsHovered(true);
    }
    onHoverChange?.(true);
  };

  const handleMouseLeave = () => {
    if (externalIsHovered === undefined) {
      setInternalIsHovered(false);
    }
    onHoverChange?.(false);
  };

  return (
    <TouchableWithoutFeedback
      onPress={handlePress}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={isRadioButton ? 'radio' : 'checkbox'}
      accessibilityState={{ checked: isChecked }}
      style={{ pointerEvents: 'auto' }}>
      <View
        style={styles.container}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        <Animated.View
          style={[
            styles.checkbox,
            isRadioButton && { width: 16, height: 16, borderRadius: 8 },
            isChecked ? styles.checked : styles.unchecked,
            isHovered &&
              !isRadioButton &&
              !isChecked &&
              (isHighlightColorGreen
                ? styles.hoveredUnchecked
                : styles.hoveredUncheckedGrey),
            isHovered &&
              !isRadioButton &&
              isChecked &&
              (isHighlightColorGreen
                ? styles.hoveredChecked
                : styles.hoveredCheckedGrey),
            { transform: [{ scale: scaleAnim }] },
          ]}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <Svg
            width={Platform.OS === 'web' ? 14 : 12}
            height={Platform.OS === 'web' ? 14 : 12}
            viewBox='0 0 24 24'
            fill='none'>
            <Path
              d='M20 6L9 17L4 12'
              stroke='white'
              strokeWidth={3}
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </Svg>

          {!isChecked && isHovered && (
            <View style={styles.previewCheckmarkContainer}>
              <Svg
                width={isRadioButton ? 12 : 14}
                height={isRadioButton ? 12 : 14}
                viewBox='0 0 24 24'
                fill='none'>
                <Path
                  d='M20 6L9 17L4 12'
                  stroke={
                    isRadioButton || !isHighlightColorGreen
                      ? theme.colors.grey03
                      : theme.colors.greenSecondary
                  }
                  strokeWidth={3}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </Svg>
            </View>
          )}
        </Animated.View>
        {label && (
          <Box pr='xxl' pl='xs' marginLeft='xxxs'>
            <Text
              onPress={() => {
                handlePress();
              }}
              accessibilityLabel={label}
              variant='bodySecondary'
              textDecorationLine='line-through'
              color='grey04'>
              {label}
            </Text>
          </Box>
        )}
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 6,
    cursor: 'pointer',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  unchecked: {
    borderColor: theme.colors.grey03,
    backgroundColor: 'white',
  },
  checked: {
    borderColor: theme.colors.greenSecondary,
    backgroundColor: theme.colors.greenSecondary,
  },
  hoveredUnchecked: {
    borderColor: theme.colors.greenSecondary,
  },
  hoveredChecked: {
    borderColor: theme.colors.greenSecondary,
    backgroundColor: 'white',
  },
  hoveredUncheckedGrey: {
    borderColor: theme.colors.grey03,
  },
  hoveredCheckedGrey: {
    borderColor: theme.colors.grey03,
    backgroundColor: 'white',
  },
  previewCheckmarkContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
